import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { BookOpen, PenLine, Search, SearchX, TriangleAlert } from "lucide-react";
import notification from "../../../../helpers/notification";
import { TspBreadcrumb, TspEmptyState, TspSkeleton } from "../../components/primitives";
import TspLoadError from "../../components/common/TspLoadError";
import useTspPermission from "../../hooks/useTspPermission";
import TSP_PERMISSIONS from "../../constants/tspPermissions";
import useTspStoriesData from "./useTspStoriesData";
import { STORY_KINDS, STORY_PAGE_SIZE, STORY_SORTS, categoriesOf } from "./storyModel";
import StoryCard from "./components/StoryCard";
import StoryDetailModal from "./components/StoryDetailModal";
import StoryFormModal from "./components/StoryFormModal";

const NO_FILTERS = { search: "", category: "", sort: "latest" };

// Shared screen behind Success Stories (kind "success") and Blogs (kind
// "blog"): same layout, different categories and copy. Search/category/sort/
// pagination all happen server-side (see storyModel.js#storyListParams) —
// this page only accumulates pages for "Load more". Reading needs
// stories.view; writing, liking and commenting need stories.create; a
// story's own edit/delete additionally follow its `capabilities` (author or
// a stories.moderate holder).
function StoryListContent({ kind }) {
  const copy = STORY_KINDS[kind];
  const [searchParams, setSearchParams] = useSearchParams();
  const openId = searchParams.get("story");

  const [searchInput, setSearchInput] = useState("");
  const [filters, setFilters] = useState(NO_FILTERS);
  const [page, setPage] = useState(1);
  const [accumulated, setAccumulated] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  const { data, loading, error, openStoryError, actions } = useTspStoriesData(kind, { ...filters, page, limit: STORY_PAGE_SIZE }, openId);
  const { hasPermission } = useTspPermission();
  const canParticipate = hasPermission(TSP_PERMISSIONS.STORIES_CREATE);

  useEffect(() => {
    if (data) {
      setHasLoadedOnce(true);
    }
  }, [data]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const trimmed = searchInput.trim();
      setFilters((prev) => (prev.search === trimmed ? prev : { ...prev, search: trimmed }));
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    if (!data) return;
    setAccumulated((prev) => (page === 1 ? data.stories : [...prev, ...data.stories.filter((story) => !prev.some((existing) => existing.id === story.id))]));
  }, [data, page]);

  const categories = useMemo(() => categoriesOf(kind), [kind]);
  const categoryByKey = useMemo(() => Object.fromEntries(categories.map((category) => [category.key, category])), [categories]);
  const openStory = data?.openStory || null;

  const setFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };
  const clearFilters = () => {
    setSearchInput("");
    setFilters(NO_FILTERS);
    setPage(1);
  };
  const handleSearchKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      const trimmed = searchInput.trim();
      setFilters((prev) => (prev.search === trimmed ? prev : { ...prev, search: trimmed }));
      setPage(1);
    }
  };
  const setOpenId = (id) => setSearchParams(id ? { story: id } : {}, { replace: true });
  const hasActiveFilters = Boolean(filters.search || filters.category);
  const showFeatured = !hasActiveFilters && filters.sort === "latest" && accumulated.length >= 3;

  const handlePublish = async (values) => {
    const entry = await actions.addStory(values);
    const title = entry?.title || entry?.data?.title || values?.title || "Story";
    notification("success", `“${title}” published.`);
    setFormOpen(false);
    clearFilters();
  };

  const heading = (
    <div className="tsp-page-heading-row">
      <div>
        <h1>{copy.label}</h1>
        <TspBreadcrumb items={["Dashboard", copy.crumb]} />
      </div>
      {canParticipate && (
        <button type="button" className="tsp-btn tsp-btn-success tsp-btn-pill" onClick={() => setFormOpen(true)}>
          <PenLine size={15} aria-hidden="true" /> {copy.write}
        </button>
      )}
    </div>
  );

  if (loading && !data && !hasLoadedOnce) {
    return (
      <div className="tsp-page tsp-stories-page">
        {heading}
        <TspSkeleton count={6} label={`Loading ${copy.label.toLowerCase()}`} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="tsp-page tsp-stories-page">
        {heading}
        <TspEmptyState
          tone="error"
          icon={TriangleAlert}
          title={`Could not load ${copy.label.toLowerCase()}`}
          message="Something went wrong. Please try again."
          action={
            <button type="button" className="tsp-btn tsp-btn-outline-primary" onClick={() => window.location.reload()}>
              Try again
            </button>
          }
        />
      </div>
    );
  }

  if (openId && openStoryError) {
    return (
      <div className="tsp-page tsp-stories-page">
        {heading}
        <TspLoadError error={openStoryError} what="this story" onRetry={() => setOpenId(null)} />
      </div>
    );
  }

  const remaining = (data?.meta?.total || 0) - accumulated.length;
  const [featured, ...rest] = showFeatured ? accumulated : [null, ...accumulated];

  return (
    <div className="tsp-page tsp-stories-page">
      {heading}

      {(data?.meta?.total ?? 0) === 0 && !hasActiveFilters ? (
        <TspEmptyState icon={BookOpen} title={copy.empty} message={copy.emptyHint} />
      ) : (
        <>
          <div className="tsp-journey-filters" role="search">
            <label className="tsp-journey-search">
              <Search size={16} aria-hidden="true" />
              <input
                type="search"
                placeholder={copy.searchPlaceholder}
                aria-label={`Search ${copy.label.toLowerCase()}`}
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                onKeyDown={handleSearchKeyDown}
              />
            </label>
            <select className="tsp-select tsp-filter-select" aria-label="Sort" value={filters.sort} onChange={(event) => setFilter("sort", event.target.value)}>
              {STORY_SORTS.map((sort) => (
                <option key={sort.value} value={sort.value}>
                  {sort.label}
                </option>
              ))}
            </select>
          </div>

          <div className="tsp-chip-filter-row" role="group" aria-label="Filter by category">
            <button type="button" className="tsp-chip-filter" aria-pressed={filters.category === ""} onClick={() => setFilter("category", "")}>
              All
            </button>
            {categories.map((category) => (
              <button key={category.key} type="button" className="tsp-chip-filter" aria-pressed={filters.category === category.key} onClick={() => setFilter("category", filters.category === category.key ? "" : category.key)}>
                {category.label}
              </button>
            ))}
          </div>

          <div className="tsp-results-bar">
            <span className="tsp-muted-text" aria-live="polite">
              Showing {accumulated.length} of {data?.meta?.total ?? accumulated.length} {((data?.meta?.total ?? accumulated.length) === 1) ? "story" : "stories"}
            </span>
            {hasActiveFilters && (
              <button type="button" className="tsp-filter-clear" onClick={clearFilters}>
                Clear filters
              </button>
            )}
          </div>

          {loading && !data ? (
            <TspSkeleton count={STORY_PAGE_SIZE} label={`Searching ${copy.label.toLowerCase()}`} />
          ) : accumulated.length === 0 ? (
            <TspEmptyState
              icon={SearchX}
              title="Nothing matches your filters"
              message="Try a different search or category, or clear the filters."
              action={
                <button type="button" className="tsp-btn tsp-btn-outline-primary" onClick={clearFilters}>
                  Clear filters
                </button>
              }
            />
          ) : (
            <>
              {featured && <StoryCard story={featured} category={categoryByKey[featured.category]} featured onOpen={setOpenId} />}
              <div className="tsp-stories-grid">
                {rest.map((entry) => (
                  <StoryCard key={entry.id} story={entry} category={categoryByKey[entry.category]} onOpen={setOpenId} />
                ))}
              </div>
              {remaining > 0 && (
                <div className="tsp-load-more">
                  <button type="button" className="tsp-btn tsp-btn-outline-primary" disabled={loading} onClick={() => setPage((prev) => prev + 1)}>
                    {loading ? "Loading…" : `Load more (${remaining} remaining)`}
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {openStory && (
        <StoryDetailModal story={openStory} category={categoryByKey[openStory.category]} canParticipate={canParticipate} onClose={() => setOpenId(null)} onLike={actions.toggleLike} onComment={(storyId, text) => actions.addComment(storyId, { text })} />
      )}
      {formOpen && <StoryFormModal kind={kind} heading={copy.write} onClose={() => setFormOpen(false)} onSubmit={handlePublish} />}
    </div>
  );
}

// The toast container lives outside the loading/error/loaded branches so it is
// never remounted (which would drop a queued toast).
export default function TspStoryListPage({ kind }) {
  return (
    <>
      <ToastContainer />
      <StoryListContent kind={kind} />
    </>
  );
}
